import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, Shuffle, LayoutGrid, Download, Lock, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ColoringArtBear from "@/components/coloring/ColoringArtBear";
import ColoringArtPumpkin from "@/components/coloring/ColoringArtPumpkin";
import ColoringArtDancing from "@/components/coloring/ColoringArtDancing";
import ColoringPaywallModal from "@/components/ColoringPaywallModal";
import ColoringShareModal from "@/components/ColoringShareModal";

const FREE_PAGE_LIMIT = 2;

// Which Stripe lookup_key does each book map to?
function lookupKeyForBook(title) {
  const t = (title || "").toLowerCase();
  if (t.includes("pumpkin")) return "coloring-book-pumpkin";
  if (t.includes("light")) return "coloring-book-light";
  if (t.includes("dancing")) return "coloring-book-dancing";
  return "coloring-book-pumpkin";
}

function viewedKey(bookId) { return `mkb-coloring-viewed-${bookId}`; }
function purchasedKey(bookId) { return `mkb-coloring-purchased-${bookId}`; }

function pickArt(title) {
  const t = (title || "").toLowerCase();
  if (t.includes("pumpkin")) return { Component: ColoringArtPumpkin, key: "pumpkin" };
  if (t.includes("light")) return { Component: ColoringArtBear, key: "bear" };
  if (t.includes("dancing")) return { Component: ColoringArtDancing, key: "dancing" };
  return { Component: ColoringArtBear, key: "bear" };
}

export default function ColoringPage() {
  const { bookId } = useParams();
  const [search] = useSearchParams();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [allBooks, setAllBooks] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [randomIdx, setRandomIdx] = useState(0);
  const [showAll, setShowAll] = useState(false);
  // Paywall state
  const [viewed, setViewed] = useState(() => new Set());     // set of page indices seen this session
  const [purchased, setPurchased] = useState(false);         // has THIS book been purchased?
  const [paywall, setPaywall] = useState({ open: false, reason: "shuffle" });
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/books/${bookId}`).then(({ data }) => setBook(data)).catch(() => setNotFound(true));
    // Fetch every book that has coloring pages — used for the bundle upsell
    api.get("/books").then(({ data }) => {
      const withPages = (data || []).filter((b) => {
        const arr = Array.isArray(b.coloring_pages) ? b.coloring_pages : [];
        return arr.length > 0;
      });
      setAllBooks(withPages);
    }).catch(() => { /* ignore */ });
  }, [bookId]);

  // Load "already viewed" pages from localStorage for this book
  useEffect(() => {
    if (!bookId) return;
    try {
      const raw = localStorage.getItem(viewedKey(bookId));
      if (raw) setViewed(new Set(JSON.parse(raw)));
    } catch { /* ignore */ }
    try {
      if (localStorage.getItem(purchasedKey(bookId)) === "yes") setPurchased(true);
    } catch { /* ignore */ }
  }, [bookId]);

  // Sync purchase status from backend (covers cross-device Google-sign-in)
  useEffect(() => {
    if (!bookId) return;
    if (!(user && user.role === "reader")) return;
    api.get("/my/purchases").then(({ data }) => {
      const owns = Array.isArray(data) && data.some((p) => p.book_id === bookId);
      if (owns) {
        setPurchased(true);
        try { localStorage.setItem(purchasedKey(bookId), "yes"); } catch { /* ignore */ }
      }
    }).catch(() => { /* silent */ });
  }, [user, bookId]);

  // Handle ?unlocked=1 return from PaymentSuccess
  useEffect(() => {
    if (search.get("unlocked") === "1") {
      setPurchased(true);
      try { localStorage.setItem(purchasedKey(bookId), "yes"); } catch { /* ignore */ }
      toast.success("Unlocked! Enjoy your whole coloring book 🎨");
      // A small delay before the share prompt so the success toast lands first
      const t = setTimeout(() => setShareOpen(true), 900);
      return () => clearTimeout(t);
    }
    if (search.get("cancelled") === "1") {
      toast("No charge was made — keep exploring for free.");
    }
  }, [search, bookId]);

  const { Component: ArtComponent } = useMemo(() => pickArt(book?.title), [book?.title]);

  // Prefer the multi-page array; fall back to the single legacy URL;
  // fall back finally to the auto-drawn SVG placeholder.
  const pages = useMemo(() => {
    if (!book) return [];
    const arr = Array.isArray(book.coloring_pages) ? book.coloring_pages.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    const single = (book.coloring_page_url || "").trim();
    return single ? [single] : [];
  }, [book]);
  const hasUpload = pages.length > 0;
  const pageCount = pages.length;
  const bookPdfUrl = (book?.coloring_book_url || "").trim();
  const lookupKey = useMemo(() => lookupKeyForBook(book?.title), [book?.title]);

  // Record a page as "viewed" in localStorage + state. Uses functional
  // setState so we don't need `viewed` in any effect deps (prevents the
  // React 18 StrictMode double-invoke from double-counting).
  const recordView = useCallback((idx) => {
    if (idx == null || purchased) return;
    setViewed((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      try { localStorage.setItem(viewedKey(bookId), JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  }, [bookId, purchased]);

  const remainingFree = Math.max(0, FREE_PAGE_LIMIT - viewed.size);
  const canShuffle = purchased || viewed.size < FREE_PAGE_LIMIT || (viewed.size === FREE_PAGE_LIMIT && !viewed.has(randomIdx));

  // Pick a random page on first load — a little surprise each visit.
  useEffect(() => {
    if (pageCount > 0) {
      const idx = Math.floor(Math.random() * pageCount);
      setRandomIdx(idx);
      recordView(idx);
    }
  }, [pageCount, recordView]);

  const shuffle = useCallback(() => {
    if (pageCount <= 1) return;
    // Paywall check: if not purchased and would exceed the free limit → offer to buy
    if (!purchased) {
      // Compute what the next page would be. If that would be a NEW unique page and
      // we've already spent all free views, block.
      let attempts = 0;
      let candidate = randomIdx;
      while (candidate === randomIdx && attempts < 40) {
        candidate = Math.floor(Math.random() * pageCount);
        attempts += 1;
      }
      const wouldBeNew = !viewed.has(candidate);
      if (wouldBeNew && viewed.size >= FREE_PAGE_LIMIT) {
        setPaywall({ open: true, reason: "shuffle" });
        return;
      }
      setRandomIdx(candidate);
      recordView(candidate);
      return;
    }
    let next = randomIdx;
    while (next === randomIdx) next = Math.floor(Math.random() * pageCount);
    setRandomIdx(next);
  }, [pageCount, purchased, randomIdx, viewed, recordView]);

  const openSeeAll = useCallback(() => {
    if (!purchased) { setPaywall({ open: true, reason: "seeAll" }); return; }
    setShowAll((v) => !v);
  }, [purchased]);

  const openDownload = useCallback((e) => {
    if (!purchased) {
      e.preventDefault();
      setPaywall({ open: true, reason: "download" });
    }
  }, [purchased]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-fredoka text-4xl" style={{ color: "#2D3055" }}>This page is hiding.</h1>
        <Link to="/books" className="btn-terracotta mt-6 inline-flex" data-testid="coloring-back-link">
          <ArrowLeft size={16} /> Back to the Books
        </Link>
      </div>
    );
  }

  if (!book) {
    return <div className="max-w-3xl mx-auto px-6 py-24 font-nunito" style={{ color: "#7E82A1" }}>Sharpening the crayons…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="coloring-page">
      <style>{`
        @media print {
          body { background: #ffffff !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .coloring-sheet { box-shadow: none !important; border: none !important; margin: 0 !important; padding: 0 !important; page-break-inside: avoid; }
          .coloring-sheet + .coloring-sheet { page-break-before: always; }
          @page { size: letter portrait; margin: 0.25in; }
        }
        .print-only { display: none; }
      `}</style>

      <div className="no-print">
        <Link
          to={`/books/${book.id}`}
          className="inline-flex items-center gap-1 font-fredoka text-sm mb-4"
          style={{ color: "#6EC4E8" }}
          data-testid="coloring-back-link"
        >
          <ArrowLeft size={16} /> Back to {book.title}
        </Link>

        <div className="block">
          <span className="font-caveat text-3xl" style={{ color: "#FF6B9D" }}>
            {pageCount > 1 ? `free preview · ${pageCount}-page coloring ebook` : "free preview"}
          </span>
        </div>
        <h1 className="font-fredoka text-4xl sm:text-5xl" style={{ color: "#2D3055" }}>
          Color along with {book.title}
        </h1>
        <p className="mt-3 max-w-2xl font-nunito text-lg" style={{ color: "#4A4E6D" }}>
          {pageCount > 1
            ? `A ${pageCount}-page coloring ebook — instant download, print at home. Peek at ${FREE_PAGE_LIMIT} pages free, then unlock the whole ebook for $3.99.`
            : "A ready-to-print keepsake for your littlest reader. Grab your favourite crayons, print in portrait, and let their imagination run wild."}
        </p>
      </div>

      {(
        <>
          <div className="no-print mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm text-white transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg,#FF6B9D 0%,#FF8C42 100%)",
                boxShadow: "0 10px 24px rgba(255,140,66,0.28)",
              }}
              data-testid="coloring-print-btn"
            >
              <Printer size={16} /> {showAll && pageCount > 1 ? `Print all ${pageCount} pages` : "Print this page"}
            </button>

            {pageCount > 1 && !showAll && (
              <button
                type="button"
                onClick={shuffle}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
                style={{
                  background: "#FFFFFF",
                  color: "#6B47C7",
                  border: "1.5px solid #C7B5FF",
                  boxShadow: "0 6px 16px rgba(107,71,199,0.14)",
                }}
                data-testid="coloring-shuffle-btn"
              >
                <Shuffle size={16} /> Try another page
                {!purchased && !canShuffle && (
                  <Lock size={12} style={{ marginLeft: 2, opacity: 0.7 }} />
                )}
              </button>
            )}

            {pageCount > 1 && (
              <button
                type="button"
                onClick={openSeeAll}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
                style={{
                  background: "#FFFFFF",
                  color: "#2D3055",
                  border: "1.5px solid #E4DFF6",
                  boxShadow: "0 6px 16px rgba(45,48,85,0.06)",
                }}
                data-testid="coloring-showall-btn"
              >
                <LayoutGrid size={16} /> {showAll ? "Show just one" : `See all ${pageCount} pages`}
                {!purchased && !showAll && <Lock size={12} style={{ marginLeft: 2, opacity: 0.7 }} />}
              </button>
            )}

            {bookPdfUrl && (
              purchased ? (
                <a
                  href={bookPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
                  style={{
                    background: "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)",
                    color: "#2D3055",
                    boxShadow: "0 10px 22px rgba(255,140,66,0.30)",
                  }}
                  data-testid="coloring-download-book-btn"
                >
                  <Download size={16} /> Download the whole book
                </a>
              ) : (
                <button
                  type="button"
                  onClick={openDownload}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
                  style={{
                    background: "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)",
                    color: "#2D3055",
                    boxShadow: "0 10px 22px rgba(255,140,66,0.30)",
                  }}
                  data-testid="coloring-download-book-btn"
                >
                  <Download size={16} /> Download the whole book
                  <Lock size={12} style={{ marginLeft: 2, opacity: 0.7 }} />
                </button>
              )
            )}

            {purchased && (
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
                style={{
                  background: "#FFFFFF",
                  color: "#D6407E",
                  border: "1.5px solid #F6ACC7",
                  boxShadow: "0 6px 16px rgba(214,64,126,0.10)",
                }}
                data-testid="coloring-share-btn-toolbar"
              >
                <Share2 size={16} /> Share
              </button>
            )}

            <span className="font-caveat text-xl" style={{ color: "#7E82A1" }}>
              Portrait · Letter · No color ink needed ✿
            </span>
          </div>

          {!purchased && pageCount > 1 && (
            <div
              className="no-print mt-4 rounded-full inline-flex items-center gap-2 px-4 py-2 text-xs font-fredoka"
              style={{
                background: remainingFree > 0 ? "#FFF3F8" : "#FFE1EC",
                color: remainingFree > 0 ? "#D6407E" : "#B02760",
                border: "1px solid #F6ACC7",
              }}
              data-testid="coloring-free-badge"
            >
              {remainingFree > 0
                ? `${remainingFree} free page${remainingFree === 1 ? "" : "s"} left · unlock all ${pageCount} for $3.99`
                : `You've enjoyed your ${FREE_PAGE_LIMIT} free pages — unlock all ${pageCount} for $3.99`}
            </div>
          )}

          {hasUpload ? (
            (showAll ? pages : [pages[randomIdx] || pages[0]]).map((url, idx) => {
              const realIdx = showAll ? idx : randomIdx;
              const isPdf = /\.pdf(\?|$)/i.test(url);
              return (
                <div
                  key={`${url}-${idx}`}
                  className="coloring-sheet mt-6 rounded-3xl overflow-hidden"
                  style={{
                    background: "#FFFFFF",
                    boxShadow: "0 30px 80px rgba(45,48,85,0.10)",
                    border: "1px solid #E4DFF6",
                    maxWidth: "816px",
                    margin: "0 auto",
                  }}
                  data-testid={idx === 0 ? "coloring-sheet" : `coloring-sheet-${idx + 1}`}
                >
                  {pageCount > 1 && (
                    <div
                      className="no-print px-5 py-3 flex items-center justify-between border-b"
                      style={{ borderColor: "#F0EBFF", background: "#FFF8FB" }}
                    >
                      <span className="font-fredoka text-sm" style={{ color: "#7E82A1" }}>
                        Page {realIdx + 1} of {pageCount}
                      </span>
                      <span className="font-caveat text-lg" style={{ color: "#FF6B9D" }}>{book.title}</span>
                    </div>
                  )}
                  {isPdf ? (
                    <iframe
                      src={url}
                      title={`${book.title} — coloring page ${realIdx + 1}`}
                      style={{ width: "100%", height: "1056px", border: "none", display: "block" }}
                      data-testid={`coloring-pdf-${realIdx + 1}`}
                    />
                  ) : (
                    <img
                      src={url}
                      alt={`${book.title} coloring page ${realIdx + 1}`}
                      style={{ width: "100%", height: "auto", display: "block", background: "#FFFFFF" }}
                      data-testid={idx === 0 ? "coloring-uploaded-img" : `coloring-uploaded-img-${idx + 1}`}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div
              className="coloring-sheet mt-6 rounded-3xl overflow-hidden"
              style={{
                background: "#FFFFFF",
                boxShadow: "0 30px 80px rgba(45,48,85,0.10)",
                border: "1px solid #E4DFF6",
                maxWidth: "816px",
                margin: "0 auto",
              }}
              data-testid="coloring-sheet"
            >
              <ArtComponent />
            </div>
          )}
        </>
      )}

      <ColoringPaywallModal
        open={paywall.open}
        onClose={() => setPaywall({ open: false, reason: paywall.reason })}
        book={book}
        lookupKey={lookupKey}
        priceLabel="$3.99"
        pageCount={pageCount}
        reason={paywall.reason}
        allBooks={allBooks}
      />

      <ColoringShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        book={book}
      />
    </div>
  );
}
